// Línea "Información adicional" de la etiqueta de envío.
//
// La alimentan DOS campos distintos del paquete, y los dos le sirven al repartidor:
//   - `shipping_address.comment` → referencia de la dirección ("Dpto: 1207")
//   - `comments`                 → comentario del pedido ("SKU 111111 - Producto Alpha")
//
// Cada generador de etiqueta elegía uno y descartaba el otro, con precedencias
// distintas entre sí: el botón "Térmica .zpl" mostraba `comments` y el de "Texto
// plano .txt" mostraba `shipping_address.comment`, para el mismo paquete. Acá se
// concatenan los dos, que es lo que se quiere ver impreso.
//
// El mismo criterio está replicado en microservice-reports (etiqueta en PDF) y en
// application-server-v2 (etiqueta ZPL de integraciones). Los repos no comparten
// paquete: si cambia la regla acá, hay que cambiarla allá.

import { zplSafe } from "./zplSafe";

const SEPARATOR = " | ";

/** Normaliza a string recortado. `null`, `undefined`, `""` y `"   "` valen lo mismo: ausente. */
const present = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

/**
 * Concatena los dos campos, omitiendo los ausentes.
 *
 * @returns {string} `"Dpto: 1207 | SKU 111111 - Producto Alpha"`, o solo el presente
 *   si falta uno, o `""` si faltan los dos (la etiqueta imprime el rótulo sin valor).
 */
export const buildAdditionalInfo = (addressComment, packageComments) =>
  [present(addressComment), present(packageComments)].filter(Boolean).join(SEPARATOR);

/**
 * Caracteres que caben en el campo, ya descontado el rótulo "Información adicional: "
 * que va dentro del mismo `^FB`.
 *
 * ESTIMADO, no medido en impresora: el bloque es `^FB720,2` a `^A0N,28,28`, o sea
 * 2 líneas de 720 dots con la fuente 0 (~15 dots por carácter de ancho medio).
 * No se puede ganar una tercera línea: el campo arranca en y=1110 y la etiqueta de
 * 4×6" a 203 dpi termina en 1218.
 *
 * Importa porque ZPL recorta lo que excede el `^FB` SIN avisar. Antes entraba un solo
 * campo y casi nunca se llegaba al límite; concatenando dos, un `comments` largo lo
 * cruza. Cortar acá al menos deja el "..." a la vista.
 */
export const ZPL_INFO_MAX_CHARS = 63;

const truncateForZpl = (text, maxChars = ZPL_INFO_MAX_CHARS) => {
  if (text.length <= maxChars) return text;
  return text.slice(0, maxChars - 3).trimEnd() + "...";
};

/**
 * El campo se imprime con `^FH`, que convierte el guion bajo en prefijo hexadecimal:
 * un "SKU_111111" hace que la impresora lea "_11" como un byte de control y corrompa
 * el campo. Se escapa como su propio código (0x5F).
 *
 * Va DESPUÉS de recortar: cortar sobre el texto ya escapado podría partir un "_5F"
 * por la mitad y dejar un hexadecimal inválido.
 */
const escapeZplUnderscore = (text) => text.replace(/_/g, "_5F");

/**
 * Valor listo para inyectar en el `^FD` de la línea de información adicional.
 *
 * `zplSafe` va ANTES de recortar (quita `^` y `~`, que de otro modo cierran el
 * campo y ejecutan el resto del comentario como formato) y el escape del `_`
 * sigue yendo al final, por el mismo motivo que ya documenta arriba.
 */
export const buildAdditionalInfoForZpl = (addressComment, packageComments) =>
  escapeZplUnderscore(
    truncateForZpl(zplSafe(buildAdditionalInfo(addressComment, packageComments))),
  );
